import { Suspense, use, useRef } from "react";
import type { MouseEvent } from "react";
import clsx from "clsx";

import { type Bookmark } from "lib/bookmarks/get-bookmarks";
import { useBookmarks, useMutateBookmarks } from "lib/bookmarks/use-bookmarks";
import { getConfig } from "lib/get-config";
import { useTranslation } from "lib/i18n/use-translation";
import { ModalsContext } from "lib/modals/use-modals";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";
import { NotificationsContext } from "lib/notifications/use-notifications";
import { extractVisibleText } from "lib/text/text-content";
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  DialogTrigger,
} from "lib/ui/dialog";
import { FloatingMenu, useFloatingMenu } from "lib/ui/floating-menu";

import BookmarksIcon from "assets/icons/bookmarks.svg?react";
import DotsBurgerIcon from "assets/icons/dotsburger.svg?react";
import TrashIcon from "assets/icons/trash.svg?react";
import XIcon from "assets/icons/x.svg?react";
import css from "./bookmarks.module.scss";
import { Quotes } from "./quotes";

export function Bookmarks() {
  const { user } = getConfig();
  const { bookmarks } = useBookmarks();
  const { deleteBookmark } = useMutateBookmarks();
  const { updateBookmark } = use(NavigationContext)!;
  const { bookmarksPanel, setBookmarksPanel } = use(ModalsContext)!;
  const { showToast } = use(NotificationsContext)!;

  const { isOpen, activeTab } = bookmarksPanel;

  const { t } = useTranslation();
  const label = t("Закладки и цитаты");

  function setIsOpen(isOpen: boolean) {
    setBookmarksPanel(prev => ({ ...prev, isOpen }));
  }

  function onBookmarkClick(xpointer: string) {
    updateBookmark(xpointer);
    setIsOpen(false);
  }

  async function onDeleteClick(e: MouseEvent, bookmarkId: string) {
    e.stopPropagation();
    const [, error] = await deleteBookmark(bookmarkId);
    if (error) {
      showToast(t("Не удалось удалить закладку"));
    }
  }

  return (
    <Dialog isOpen={isOpen} setIsOpen={setIsOpen} triggerLabel={label}>
      <DialogTrigger
        className={clsx(css.trigger, isOpen && css.active)}
        disabled={!user}
      >
        <BookmarksIcon width={24} height={24} />
      </DialogTrigger>
      <DialogOverlay className={css.overlay} />
      <DialogPanel className={clsx(css.drawer, isOpen && css.open)}>
        <div className={css.header}>
          <button
            onClick={() => setIsOpen(false)}
            className={css.closeButton}
            aria-label={t("Закрыть")}
            title={t("Закрыть")}
          >
            <XIcon />
          </button>
          <div className={css.tabs} role="tablist" aria-label={label}>
            <button
              className={clsx(css.tab, activeTab === "bookmarks" && css.active)}
              role="tab"
              aria-selected={activeTab === "bookmarks"}
              onClick={() =>
                setBookmarksPanel(prev => ({
                  ...prev,
                  activeTab: "bookmarks",
                }))
              }
            >
              {t("Закладки")}
            </button>
            <button
              className={clsx(css.tab, activeTab === "quotes" && css.active)}
              role="tab"
              aria-selected={activeTab === "quotes"}
              onClick={() =>
                setBookmarksPanel(prev => ({
                  ...prev,
                  activeTab: "quotes",
                }))
              }
            >
              {t("Цитаты")}
            </button>
          </div>
        </div>
        <Suspense fallback={null}>
          {activeTab === "bookmarks" ? (
            <div className={css.content}>
              {bookmarks.length > 0 ? (
                <ul className={css.list}>
                  {bookmarks.map(bookmark => (
                    <BookmarkListItem
                      key={bookmark.id}
                      bookmark={bookmark}
                      onClick={() => onBookmarkClick(bookmark.xpointer)}
                      onDelete={e => onDeleteClick(e, bookmark.id)}
                    />
                  ))}
                </ul>
              ) : (
                <div>{t("Нет закладок")}</div>
              )}
            </div>
          ) : (
            <Quotes />
          )}
        </Suspense>
      </DialogPanel>
    </Dialog>
  );
}

type BookmarkListItemProps = {
  bookmark: Bookmark;
  onClick: () => void;
  onDelete: (e: MouseEvent) => void;
};

function BookmarkListItem({
  bookmark,
  onClick,
  onDelete,
}: BookmarkListItemProps) {
  const { t } = useTranslation();
  const { xpointerToContentNode, xpointerToIndex, context } = usePaths();
  const triggerRef = useRef<HTMLDivElement>(null);
  const actionsMenu = useFloatingMenu(triggerRef);

  const node = xpointerToContentNode(bookmark.xpointer);
  const text = node ? extractVisibleText(node) : "";
  const chapterTitle = bookmark.chapter_title || t("Без заголовка");
  const index = xpointerToIndex(bookmark.xpointer);
  const isNavigable =
    index !== null && context.layout?.elements[index] !== undefined;

  return (
    <li className={css.bookmark}>
      <button
        className={css.bookmarkButton}
        onClick={onClick}
        disabled={!isNavigable}
      >
        <span className={css.bookmarkItem}>
          <span className={css.chapterTitle}>{chapterTitle}</span>
          <span className={css.bookmarkText}>{text}</span>
        </span>
      </button>
      <div className={css.actions}>
        <div ref={triggerRef} className={css.menuContainer}>
          <button
            className={css.dotsburger}
            onClick={e => {
              e.stopPropagation();
              actionsMenu.toggle();
            }}
            aria-label={t("Действия")}
            aria-haspopup="menu"
            aria-expanded={Boolean(actionsMenu.anchor)}
          >
            <DotsBurgerIcon />
          </button>
        </div>
        {actionsMenu.anchor && (
          <FloatingMenu anchor={actionsMenu.anchor} onClose={actionsMenu.close}>
            <button
              className={css.menuItem}
              onClick={() => {
                onClick();
                actionsMenu.close();
              }}
            >
              {t("Перейти к закладке")}
            </button>
          </FloatingMenu>
        )}
        <button
          className={css.deleteButton}
          onClick={onDelete}
          aria-label={t("Удалить закладку")}
          title={t("Удалить закладку")}
        >
          <TrashIcon />
        </button>
      </div>
    </li>
  );
}
